import { useEffect, useState } from "react";

import { cn } from "@/utils/cn";

export type ThinkingEntryKind =
  | "stage"
  | "decomposition"
  | "subagent_start"
  | "subagent_done"
  | "tool_call"
  | "tool_result"
  | "draft"
  | "critic"
  | "warning"
  | "error"
  | "subagent_spawned"
  | "subagent_completed"
  | "spawn_refused";

export interface ThinkingEntry {
  id: string;
  kind: ThinkingEntryKind;
  label: string;
  detail?: string;
  body?: string;
  timestamp: number;
}

interface Props {
  entries: ThinkingEntry[];
  isStreaming: boolean;
  startedAt: number | null;
  className?: string;
}

const KIND_LABEL: Record<ThinkingEntryKind, string> = {
  stage: "stage",
  decomposition: "plan",
  subagent_start: "agent",
  subagent_done: "agent",
  tool_call: "tool",
  tool_result: "result",
  draft: "draft",
  critic: "critic",
  warning: "warning",
  error: "error",
  subagent_spawned: "spawn",
  subagent_completed: "spawn",
  spawn_refused: "spawn",
};

/**
 * Four families, so a reader can tell at a glance what the agent *did* from
 * what it *thought*. Colour alone was not enough — a machine call and a piece
 * of reasoning now differ in typeface and rule as well.
 *
 *   stage   — a section header with a hairline across the panel
 *   tool    — mono, neutral rule: a call out to something
 *   agent   — serif, oxblood rule: the agent's own reasoning
 *   problem — alarm wash
 */
type EntryFamily = "stage" | "tool" | "agent" | "problem";

const KIND_FAMILY: Record<ThinkingEntryKind, EntryFamily> = {
  stage: "stage",
  decomposition: "stage",
  tool_call: "tool",
  tool_result: "tool",
  subagent_start: "agent",
  subagent_done: "agent",
  subagent_spawned: "agent",
  subagent_completed: "agent",
  spawn_refused: "agent",
  draft: "agent",
  critic: "agent",
  warning: "problem",
  error: "problem",
};

/** A failed tool result reads as a problem, not as a routine call. */
function familyOf(entry: ThinkingEntry): EntryFamily {
  if (entry.kind === "tool_result" && /✗|failed|error/i.test(entry.label)) return "problem";
  return KIND_FAMILY[entry.kind];
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

/**
 * No single agent turn plausibly runs longer than this. Anything above it means
 * we are measuring the wrong thing, and showing nothing beats showing nonsense.
 */
const MAX_PLAUSIBLE_THINK_MS = 2 * 60 * 60 * 1000;

/**
 * How long the agent actually thought.
 *
 * While streaming this is a live stopwatch. Once finished it is derived from
 * the events themselves — each carries the backend's `elapsed_ms` offset from
 * the start of the turn, so the last one *is* the duration.
 *
 * It must never be `now - startedAt` for a finished turn: history hydrates
 * `startedAt` from the turn's `created_at`, so that subtraction yields the age
 * of the conversation. A day-old turn read as 86,400s and older ones ran into
 * the millions, which also made the panel look like it was still running.
 */
function thinkDurationMs(
  entries: ThinkingEntry[],
  startedAt: number | null,
  isStreaming: boolean,
  now: number,
): number | null {
  if (startedAt == null) return null;

  if (isStreaming) {
    const live = now - startedAt;
    return live >= 0 && live < MAX_PLAUSIBLE_THINK_MS ? live : null;
  }

  if (entries.length === 0) return null;
  const last = Math.max(...entries.map((e) => e.timestamp));
  const span = last - startedAt;
  return span > 0 && span < MAX_PLAUSIBLE_THINK_MS ? span : null;
}

// The spec's in-flight indicator: PONDERING resolving one character at a time
// out of a field of noise, on a 70ms tick.
const SCRAMBLE_WORD = "PONDERING";
const SCRAMBLE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>/*#@";
const SCRAMBLE_TICK_MS = 70;
/** Ticks each character holds before the next one resolves. */
const TICKS_PER_CHAR = 2;
/** Ticks the fully-resolved word holds before the cycle restarts. */
const HOLD_TICKS = 12;

function ScrambleLoader() {
  const [text, setText] = useState(SCRAMBLE_WORD);

  useEffect(() => {
    // Anyone who has asked for less motion gets the word, not the noise.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) return;

    let tick = 0;
    const id = window.setInterval(() => {
      tick += 1;
      const revealed = Math.floor(tick / TICKS_PER_CHAR);
      if (revealed > SCRAMBLE_WORD.length + HOLD_TICKS) {
        tick = 0;
        return;
      }
      setText(
        SCRAMBLE_WORD.split("")
          .map((ch, i) =>
            i < revealed
              ? ch
              : SCRAMBLE_ALPHABET[Math.floor(Math.random() * SCRAMBLE_ALPHABET.length)],
          )
          .join(""),
      );
    }, SCRAMBLE_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <span className="flex items-center gap-2.5">
      {/* The scrambling text is decoration; screen readers get the plain word. */}
      <span aria-hidden className="font-mono tracking-[0.2em] text-oxblood-500">
        {text}
      </span>
      <span className="sr-only">Pondering</span>
      <span aria-hidden className="font-serif text-[0.8rem] italic text-warmgray-400">
        pondering…
      </span>
    </span>
  );
}

export function ThinkingPanel({ entries, isStreaming, startedAt, className }: Props) {
  // Starts closed. It used to open and then auto-collapse when the answer
  // landed, which flashed the whole transcript open on every turn.
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isStreaming) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [isStreaming]);

  if (!entries.length && !isStreaming) return null;

  const durationMs = thinkDurationMs(entries, startedAt, isStreaming, now);
  const lastStage =
    [...entries].reverse().find((e) => e.kind === "stage")?.label ??
    (isStreaming ? "starting" : "done");

  return (
    <details
      open={expanded}
      onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
      className={cn("panel-atelier overflow-hidden", className)}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 border-b border-ruleline px-5 py-3 transition-colors duration-200 ease-atelier hover:bg-parchment-100">
        <span className="flex min-w-0 items-center gap-3">
          {isStreaming ? (
            <ScrambleLoader />
          ) : (
            // No duration rather than a wrong one.
            <span className="label-mono">
              {durationMs == null
                ? "Thought process"
                : `Thought for ${formatElapsed(Math.round(durationMs / 1000))}`}
            </span>
          )}
          <span className="truncate font-mono text-[0.65rem] text-warmgray-400">{lastStage}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3 font-mono text-[0.7rem]">
          <span className="text-warmgray-400">
            {entries.length} step{entries.length === 1 ? "" : "s"}
          </span>
          {isStreaming && durationMs != null ? (
            <span className="text-oxblood-400">
              {formatElapsed(Math.floor(durationMs / 1000))}
            </span>
          ) : null}
          <span
            className={cn(
              "text-warmgray-400 transition-transform duration-300 ease-atelier",
              expanded && "rotate-180",
            )}
          >
            ▾
          </span>
        </span>
      </summary>

      {/* Capped at 40vh so a long run cannot push the answer off screen; the
          reasoning scrolls within its own panel instead. */}
      <ol
        data-testid="thinking-entries"
        className="animate-ery-rise max-h-[40vh] overflow-y-auto"
      >
        {entries.map((entry) => {
          const family = familyOf(entry);
          return (
          <li
            key={entry.id}
            data-family={family}
            className={cn(
              "px-5 py-2.5 text-sm",
              // A stage is a header, not a list item — it gets a hairline
              // across the panel and sits on the parchment tint.
              family === "stage" && "border-y border-ruleline bg-parchment-100 py-2",
              family === "tool" && "border-l-2 border-warmgray-200",
              family === "agent" && "border-l-2 border-oxblood-200",
              family === "problem" && "border-l-2 border-oxblood-300 bg-oxblood-50",
            )}
          >
            <div className="flex items-baseline gap-3">
              <span
                className={cn(
                  "shrink-0 font-mono text-[0.62rem] uppercase tracking-[0.16em]",
                  family === "stage" && "text-oxblood-500",
                  family === "tool" && "text-warmgray-400",
                  family === "agent" && "text-oxblood-400",
                  family === "problem" && "text-oxblood-500",
                )}
              >
                {KIND_LABEL[entry.kind]}
              </span>
              <span
                className={cn(
                  "min-w-0",
                  // Machine calls read as mono; the agent's own steps read as
                  // prose. That difference is doing the real work here.
                  family === "tool"
                    ? "font-mono text-[0.78rem] text-espresso-700"
                    : "font-serif text-espresso-700",
                  family === "stage" && "font-medium uppercase tracking-[0.06em]",
                  family === "problem" && "text-oxblood-600",
                )}
              >
                {entry.label}
              </span>
              {entry.detail ? (
                <span className="font-mono text-xs text-warmgray-400">· {entry.detail}</span>
              ) : null}
            </div>
            {entry.body ? (
              <pre
                className={cn(
                  "mt-2 whitespace-pre-wrap break-words border-l-2 border-ruleline pl-3 leading-relaxed text-warmgray-500",
                  family === "tool"
                    ? "font-mono text-[0.72rem]"
                    : "font-serif text-[0.92rem]",
                )}
              >
                {entry.body}
              </pre>
            ) : null}
          </li>
          );
        })}
        {isStreaming ? (
          <li className="px-5 py-3 text-xs italic text-warmgray-400">
            Awaiting next signal from the agents…
          </li>
        ) : null}
      </ol>
    </details>
  );
}
